import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, useIdentityAuth as useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "@/lib/toast";
import { Loader2, AlertTriangle, RefreshCw } from "lucide-react";

import CurrencySelect from "./CurrencySelect";
import { validExchangeRate } from "../lib/currencies";
import { getWorkspaceSession } from "../lib/workspaceSession";
import { useWorkspaces } from "../contexts/WorkspaceContext";

const ProfileSettings = ({ open, onOpenChange, onCurrencyChange }) => {
  const { user, logout } = useAuth();
  const workspaces = useWorkspaces();
  const selectedBoard = workspaces?.active;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    currency: "COP",
    chat_mode: "roast",
    avatar: ""
  });
  
  // Reset Account State
  const [showResetView, setShowResetView] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [resetOtp, setResetOtp] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  // Delete Account State
  const [showDeleteView, setShowDeleteView] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteOtp, setDeleteOtp] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Currency Conversion State
  const [showCurrencyConversion, setShowCurrencyConversion] = useState(false);
  const [exchangeRate, setExchangeRate] = useState("");
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [targetCurrency, setTargetCurrency] = useState("");
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        currency: user.currency || "COP",
        chat_mode: user.chat_mode || "roast",
        avatar: user.avatar || ""
      });
    }
  }, [user]);

  useEffect(() => {
    if (!open || !user) return;
    let active = true;
    setSessionsLoading(true);
    api.get("/auth/sessions")
      .then(({ data }) => { if (active) setSessions(data.sessions || []); })
      .catch(() => { if (active) setSessions([]); })
      .finally(() => { if (active) setSessionsLoading(false); });
    return () => { active = false; };
  }, [open, user]);

  const revokeSession = async (session) => {
    try {
      await api.delete(`/auth/sessions/${session.id}`);
      if (session.current) {
        await logout();
        onOpenChange(false);
        navigate("/login");
        return;
      }
      setSessions(items => items.filter(item => item.id !== session.id));
      toast.success("Sesión cerrada en ese dispositivo");
    } catch (error) {
      toast.error(error.response?.data?.detail || "No pudimos cerrar esa sesión");
    }
  };

  const revokeOtherSessions = async () => {
    try {
      await api.post("/auth/sessions/revoke-others");
      setSessions(items => items.filter(item => item.current));
      toast.success("Cerramos las demás sesiones");
    } catch (error) {
      toast.error(error.response?.data?.detail || "No pudimos cerrar las demás sesiones");
    }
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setShowResetView(false);
      setShowCurrencyConversion(false);
      setShowDeleteView(false);
      setResetStep(1);
      setResetOtp("");
      setDeleteStep(1);
      setDeleteOtp("");
    }
  }, [open]);

  const fetchExchangeRate = async (base, target) => {
    setCurrencyLoading(true);
    setExchangeRate("");
    try {
      const res = await api.get(`/auth/exchange-rate?base=${base}&target=${target}`);
      if (validExchangeRate(res.data.rate)) setExchangeRate(res.data.rate);
      else toast.error("No hay una tasa disponible. Ingresa la tasa manualmente para continuar.");
    } catch (error) {
      console.error("Error fetching rate", error);
      toast.error("Error al obtener tarifa, usa el valor manual");
    } finally {
      setCurrencyLoading(false);
    }
  };

  const handleConfirmCurrencyConversion = async () => {
    if (!validExchangeRate(exchangeRate)) {
      toast.error("La tarifa debe ser mayor a 0");
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/auth/convert-currency", {
        new_currency: targetCurrency,
        exchange_rate: Number(exchangeRate)
      });
      
      const updates = {};
      if (formData.name !== user?.name) updates.name = formData.name;
      if (formData.email !== user?.email) updates.email = formData.email;
      if (formData.chat_mode !== (user?.chat_mode || "roast")) updates.chat_mode = formData.chat_mode;
      if (formData.avatar !== (user?.avatar || "")) updates.avatar = formData.avatar;
      
      if (Object.keys(updates).length > 0) {
        await api.put("/auth/me", updates);
      }
      
      toast.success("Moneda actualizada y registros convertidos");
      onOpenChange(false);
      if (onCurrencyChange) onCurrencyChange(targetCurrency);
      window.location.reload();
    } catch (error) {
      toast.error("Error al convertir moneda");
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (formData.currency !== user?.currency) {
      setTargetCurrency(formData.currency);
      setShowCurrencyConversion(true);
      fetchExchangeRate(user?.currency || "COP", formData.currency);
      return;
    }

    setLoading(true);
    try {
      const updates = {};
      if (formData.name !== user?.name) updates.name = formData.name;
      if (formData.email !== user?.email) updates.email = formData.email;
      if (formData.chat_mode !== (user?.chat_mode || "roast")) updates.chat_mode = formData.chat_mode;
      if (formData.avatar !== (user?.avatar || "")) updates.avatar = formData.avatar;

      if (Object.keys(updates).length === 0) {
        toast.info("No hay cambios para guardar");
        setLoading(false);
        return;
      }

      await api.put("/auth/me", updates);
      toast.success("Perfil actualizado");
      onOpenChange(false);
      window.location.reload();
    } catch (error) {
      toast.error("Error al actualizar perfil");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestResetOtp = async () => {
    setResetLoading(true);
    try {
      const res = await api.post("/auth/request-reset-otp");
      if (res.data.whatsapp_sent) {
        setResetStep(2);
        toast.success("Código de confirmación enviado a tu WhatsApp");
      } else {
        toast.error(res.data.whatsapp_error || "Error al enviar código por WhatsApp");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar código");
    } finally {
      setResetLoading(false);
    }
  };

  const handleConfirmReset = async () => {
    if (!resetOtp || resetOtp.length !== 6) {
      toast.error("Ingresa el código de 6 dígitos");
      return;
    }
    
    setResetLoading(true);
    try {
      await api.post("/auth/reset-account", { otp: resetOtp, workspace_id: selectedBoard?.id || getWorkspaceSession().id || `personal:${user.id}` });
      toast.success("Tablero restablecido exitosamente");
      onOpenChange(false);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Código inválido o expirado");
    } finally {
      setResetLoading(false);
    }
  };

  const handleBackToProfile = () => {
    setShowResetView(false);
    setResetStep(1);
    setResetOtp("");
  };

  // ── Delete Account handlers ──
  const handleRequestDeleteOtp = async () => {
    setDeleteLoading(true);
    try {
      const res = await api.post("/auth/request-delete-otp");
      if (res.data.whatsapp_sent) {
        setDeleteStep(2);
        toast.success("Código de confirmación enviado a tu WhatsApp");
      } else {
        toast.error(res.data.whatsapp_error || "Error al enviar código por WhatsApp");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar código");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteOtp || deleteOtp.length !== 6) {
      toast.error("Ingresa el código de 6 dígitos");
      return;
    }
    setDeleteLoading(true);
    try {
      await api.post("/auth/delete-account", { otp: deleteOtp });
      toast.success("Cuenta eliminada. Hasta pronto.");
      onOpenChange(false);
      // Logout and redirect to login
      if (logout) logout();
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Código inválido o expirado");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBackFromDelete = () => {
    setShowDeleteView(false);
    setDeleteStep(1);
    setDeleteOtp("");
  };

  // Convert Currency Modal View
  if (showCurrencyConversion) {
    const baseCurrency = user?.currency || "COP";
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) onOpenChange(false); }}>
        <DialogContent
          hideClose
          className="p-0 border-0"
          style={{
            width: "522px",
            maxWidth: "95vw",
            overflow: "visible",
            background: "#FFFFFF",
            boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
            borderRadius: "20px",
            padding: "40px 30px 24px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "32px", width: "100%", position: "relative" }}>
            <button
              onClick={() => onOpenChange(false)}
              style={{
                position: "absolute",
                top: "-24px",
                right: "-12px",
                width: "24px",
                height: "24px",
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="#255E99" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <h2 style={{
              width: "100%",
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: "24px",
              lineHeight: "33px",
              textAlign: "center",
              color: "#1B2559",
              margin: 0,
            }}>
              Convertir Moneda
            </h2>

            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              width: "100%",
              maxWidth: "398px",
            }}>
              
              <div style={{ textAlign: "center", fontSize: "15px", color: "#5C5C5C", fontFamily: "'Manrope', sans-serif", marginBottom: "8px" }}>
                Estás a punto de cambiar tu moneda de <strong style={{ color: "#396BE9" }}>{baseCurrency}</strong> a <strong style={{ color: "#396BE9" }}>{targetCurrency}</strong>.
              </div>

              {/* Info Box */}
              <div style={{ background: "#F4F7FE", borderRadius: "16px", padding: "16px", width: "100%", border: "1px solid #E0E5F2", textAlign: "center" }}>
                <p style={{ fontSize: "14px", color: "#396BE9", fontWeight: 700, marginBottom: "8px", fontFamily: "'Manrope', sans-serif", margin: 0 }}>
                  Tus movimientos se ajustarán a esta tarifa
                </p>
                <p style={{ fontSize: "12px", color: "#A3AED0", fontFamily: "'Manrope', sans-serif", margin: "4px 0 0 0" }}>
                  Revisa la tasa antes de confirmar. Si no está disponible, debes ingresarla manualmente.
                </p>
              </div>

              {/* Input Field */}
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%", position: "relative" }}>
                <label style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 800,
                  fontSize: "16px",
                  lineHeight: "22px",
                  color: "#5C5C5C",
                }}>
                  Tarifa de Cambio ({baseCurrency} x {targetCurrency})
                </label>
                <Input
                  id="exchange-rate"
                  type="number"
                  step="0.000001"
                  min="0.000001"
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  disabled={currencyLoading}
                  style={{
                    boxSizing: "border-box",
                    width: "100%",
                    height: "48px",
                    padding: "12px",
                    border: "1px solid #DDDDDD",
                    borderRadius: "20px",
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#4A4A4A",
                    outline: "none",
                    background: "#FFFFFF",
                  }}
                />
                {currencyLoading && (
                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#396BE9", position: "absolute", right: "16px", top: "45px" }} />
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: "flex", gap: "12px", width: "100%", marginTop: "16px" }}>
                <button
                  type="button"
                  onClick={() => setShowCurrencyConversion(false)}
                  disabled={loading || currencyLoading}
                  style={{
                    flex: 1,
                    height: "54px",
                    borderRadius: "16px",
                    border: "none",
                    background: "transparent",
                    color: "#8E8E8E",
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 700,
                    fontSize: "16px",
                    cursor: "pointer",
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmCurrencyConversion}
                  disabled={loading || currencyLoading || !validExchangeRate(exchangeRate)}
                  style={{
                    flex: 1,
                    height: "54px",
                    borderRadius: "16px",
                    border: "none",
                    background: "#E1E9FC",
                    color: "#396BE9",
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 600,
                    fontSize: "16px",
                    cursor: (loading || currencyLoading || !validExchangeRate(exchangeRate)) ? "not-allowed" : "pointer",
                    opacity: (loading || currencyLoading || !validExchangeRate(exchangeRate)) ? 0.6 : 1,
                    transition: "all 0.15s ease",
                  }}
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Convertir"}
                </button>
              </div>

            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Delete Account View
  if (showDeleteView) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md bg-white border-outline-variant/20 rounded-3xl p-8 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-headline font-bold text-xl" style={{ color: '#7C0000' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="#7C0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 11v6M14 11v6" stroke="#7C0000" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Eliminar Cuenta
            </DialogTitle>
            <DialogDescription>
              {deleteStep === 1 ? (
                "Esta acción es permanente e irreversible. Debes confirmarla con un código enviado a tu WhatsApp. Perderás tu cuenta, tus datos y cualquier plan vigente, aunque aún tenga tiempo disponible."
              ) : (
                "Ingresa el código de 6 dígitos enviado a tu WhatsApp para eliminar tu cuenta definitivamente. También perderás tu plan vigente y el tiempo restante."
              )}
            </DialogDescription>
          </DialogHeader>

          {deleteStep === 1 ? (
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-xl border" style={{ background: '#FFF0F0', borderColor: '#FFD0D0' }}>
                <p className="text-sm font-bold mb-2" style={{ color: '#7C0000' }}>🗑️ Se eliminará permanentemente:</p>
                <ul className="text-sm list-disc list-inside space-y-1" style={{ color: '#B00000' }}>
                  <li>Tu perfil y acceso a Aorra</li>
                  <li>Tu plan vigente y todo el tiempo restante</li>
                  <li>Todos tus tableros propios y el acceso a tableros compartidos</li>
                  <li>Todas tus transacciones e historial</li>
                  <li>Tus deudas, tarjetas y suscripciones</li>
                  <li>Tus metas de ahorro y activos</li>
                  <li>Tus viajes y gastos en tus tableros propios</li>
                </ul>
              </div>
              <div className="p-3 rounded-xl border" style={{ background: '#FFF8E1', borderColor: '#FFE082' }}>
                <p className="text-xs" style={{ color: '#795548' }}>
                  ⚠️ Los datos de tableros que pertenecen a otras personas se conservarán. Si vuelves a registrarte, tendrás una cuenta nueva vacía, sin recuperar tu plan anterior.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="delete-otp">Código de Confirmación</Label>
                <Input
                  id="delete-otp"
                  placeholder="000000"
                  value={deleteOtp}
                  onChange={(e) => setDeleteOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-widest font-mono h-12"
                  maxLength={6}
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleBackFromDelete} disabled={deleteLoading}>
              Volver
            </Button>
            {deleteStep === 1 ? (
              <Button
                onClick={handleRequestDeleteOtp}
                disabled={deleteLoading}
                style={{ background: '#B00000' }}
                className="text-white hover:opacity-90"
              >
                {deleteLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Enviar Código
              </Button>
            ) : (
              <Button
                onClick={handleConfirmDelete}
                disabled={deleteLoading || deleteOtp.length !== 6}
                style={{ background: '#B00000' }}
                className="text-white hover:opacity-90"
              >
                {deleteLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Eliminar Cuenta
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Reset Account View
  if (showResetView) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md bg-white border-outline-variant/20 rounded-3xl p-8 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600 font-headline font-bold text-xl">
              <AlertTriangle className="w-5 h-5" />
              Restablecer tablero
            </DialogTitle>
            <DialogDescription>
              {resetStep === 1 ? (
                `Esta acción eliminará los datos financieros del tablero ${selectedBoard?.name || 'Personal'}. Los demás tableros se mantendrán.`
              ) : (
                "Ingresa el código de 6 dígitos enviado a tu WhatsApp."
              )}
            </DialogDescription>
          </DialogHeader>

          {resetStep === 1 ? (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-rose-50 rounded-xl border border-rose-200">
                <p className="text-sm text-rose-700 font-medium mb-2">
                  ⚠️ Se eliminarán:
                </p>
                <ul className="text-sm text-rose-600 list-disc list-inside space-y-1">
                  <li>Las transacciones de este tablero</li>
                  <li>Sus deudas y pagos</li>
                  <li>Sus suscripciones</li>
                  <li>Sus metas de ahorro</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reset-otp">Código de Confirmación</Label>
                <Input
                  id="reset-otp"
                  placeholder="000000"
                  value={resetOtp}
                  onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-widest font-mono h-12"
                  maxLength={6}
                  data-testid="reset-otp-input"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleBackToProfile} disabled={resetLoading}>
              Volver
            </Button>
            {resetStep === 1 ? (
              <Button onClick={handleRequestResetOtp} disabled={resetLoading} className="bg-rose-600 hover:bg-rose-700 text-white">
                Enviar Código
              </Button>
            ) : (
              <Button onClick={handleConfirmReset} disabled={resetLoading || resetOtp.length !== 6} className="bg-rose-600 hover:bg-rose-700 text-white">
                Restablecer tablero
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ─── MAIN MODAL RENDER ───
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="aorra-profile-modal-content" hideClose
        onOpenAutoFocus={event => {
          // Keep the title visible and avoid opening/zooming the mobile keyboard.
          event.preventDefault();
          event.currentTarget.focus({ preventScroll: true });
        }}>
        {/* Header container */}
        <DialogHeader className="aorra-profile-modal-header">
          <DialogTitle className="aorra-profile-modal-title">
            Configuración de perfil
          </DialogTitle>
          <DialogDescription className="sr-only">Actualiza tus datos, moneda y preferencias del bot.</DialogDescription>
          {/* Custom Close Button in top right */}
          <button 
            onClick={() => onOpenChange(false)}
            className="aorra-profile-modal-close"
            title="Cerrar"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L11 11M11 1L1 11" stroke="#255E99" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </DialogHeader>

        {/* Modal Center block */}
        <div className="aorra-profile-modal-body">
          {/* Avatar and Name */}
          <div className="aorra-profile-avatar-section">
            <div className="aorra-profile-avatar-circle" style={{ overflow: "hidden" }}>
              {formData.avatar ? (
                <img src={`/avatares/${formData.avatar}`} alt="Avatar seleccionado" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <span style={{ fontSize: 36 }}>👨‍💼</span>
              )}
            </div>
            <div className="aorra-profile-user-name">
              {formData.name || "Usuario"}
            </div>
            <div style={{ fontSize: 13, color: "#5C5C5C", marginTop: 4, fontWeight: 700, fontFamily: "'Manrope', sans-serif" }}>Elige tu avatar</div>
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8, marginTop: 4, width: "100%", maxWidth: 320 }}>
              {Array.from({length: 6}).map((_, i) => (
                <div key={`mujer-${i+1}`} 
                     onClick={() => setFormData({...formData, avatar: `mujer-${i+1}.png`})}
                     style={{
                       width: 44, height: 44, borderRadius: "50%", background: "#CDF7F7", cursor: "pointer",
                       border: formData.avatar === `mujer-${i+1}.png` ? "3px solid #396BE9" : "2px solid transparent",
                       overflow: "hidden", transition: "all 0.2s ease",
                       transform: formData.avatar === `mujer-${i+1}.png` ? "scale(1.1)" : "scale(1)"
                     }}>
                  <img src={`/avatares/mujer-${i+1}.png`} alt={`Mujer ${i+1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              ))}
              {Array.from({length: 6}).map((_, i) => (
                <div key={`hombre-${i+1}`} 
                     onClick={() => setFormData({...formData, avatar: `hombre-${i+1}.png`})}
                     style={{
                       width: 44, height: 44, borderRadius: "50%", background: "#CDF7F7", cursor: "pointer",
                       border: formData.avatar === `hombre-${i+1}.png` ? "3px solid #396BE9" : "2px solid transparent",
                       overflow: "hidden", transition: "all 0.2s ease",
                       transform: formData.avatar === `hombre-${i+1}.png` ? "scale(1.1)" : "scale(1)"
                     }}>
                  <img src={`/avatares/hombre-${i+1}.png`} alt={`Hombre ${i+1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              ))}
            </div>
          </div>

          {/* Form fields */}
          <div className="aorra-profile-form">
            {/* Nombre input */}
            <div className="aorra-profile-field">
              <Label className="aorra-profile-field-label">Nombre</Label>
              <div className="aorra-profile-input-wrapper">
                <Input
                  className="aorra-profile-input"
                  id="profile-name"
                  placeholder="Nombre"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  data-testid="profile-name-input"
                />
              </div>
            </div>

            {/* Email input */}
            <div className="aorra-profile-field">
              <Label className="aorra-profile-field-label">Correo electrónico</Label>
              <div className="aorra-profile-input-wrapper">
                <Input
                  className="aorra-profile-input"
                  id="profile-email"
                  type="email"
                  placeholder="Correo electrónico"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  data-testid="profile-email-input"
                />
                {/* Pen edit icon */}
                <button type="button" className="aorra-profile-edit-icon" title="Editar">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="#396BE9" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Celular input (disabled/readonly) */}
            <div className="aorra-profile-field">
              <Label className="aorra-profile-field-label">Celular</Label>
              <div className="aorra-profile-input-wrapper" style={{ background: "#EFEAE9" }}>
                <Input
                  className="aorra-profile-input disabled"
                  value={user?.phone || ""}
                  disabled
                  readOnly
                />
              </div>
            </div>

            {/* Moneda select */}
            <div className="aorra-profile-field">
              <Label htmlFor="profile-currency" className="aorra-profile-field-label">Moneda</Label>
              <CurrencySelect id="profile-currency" data-testid="currency-select"
                className="aorra-profile-currency-trigger" modal
                value={formData.currency} disabled={loading}
                onValueChange={currency => setFormData({ ...formData, currency })} />
            </div>

            {/* Modo de Chat select */}
            <div className="aorra-profile-field mt-2">
              <Label className="aorra-profile-field-label">Personalidad del Bot</Label>
              <div className="aorra-profile-input-wrapper">
                <Select 
                  value={formData.chat_mode} 
                  onValueChange={(v) => setFormData({...formData, chat_mode: v})}
                >
                  <SelectTrigger data-testid="chat-mode-select" className="aorra-profile-select-trigger">
                    <SelectValue placeholder="Selecciona el estilo del bot" />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl">
                    <SelectItem value="roast">Roast (Sarcástico 60/40)</SelectItem>
                    <SelectItem value="serio">Asistente (Serio)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="w-full rounded-2xl border border-[#E1E9FC] bg-[#F8FAFF] p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-[#1B2559]">Dispositivos con sesión</p>
                <p className="text-xs text-[#545454]">Puedes cerrar accesos que ya no reconozcas.</p>
              </div>
              {sessions.filter(item => !item.current).length > 0 && (
                <button type="button" onClick={revokeOtherSessions} className="text-xs font-bold text-[#396BE9] hover:underline">
                  Cerrar las demás
                </button>
              )}
            </div>
            {sessionsLoading ? (
              <p className="text-xs text-[#545454]">Cargando dispositivos…</p>
            ) : sessions.length === 0 ? (
              <p className="text-xs text-[#545454]">No hay otras sesiones activas.</p>
            ) : (
              <div className="space-y-2">
                {sessions.map(item => (
                  <div key={item.id} className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-bold text-[#1B2559]">
                        {item.browser} en {item.platform} {item.current ? "· Este dispositivo" : ""}
                      </p>
                      <p className="text-[11px] text-[#6B7280]">
                        {item.trusted ? "Recordado por 7 días" : "Sesión temporal"}
                      </p>
                    </div>
                    <button type="button" onClick={() => revokeSession(item)} className="shrink-0 text-xs font-bold text-[#B00000] hover:underline">
                      Cerrar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="aorra-profile-buttons">
            <button
              type="button"
              onClick={handleSave}
              className="aorra-profile-btn-primary"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Guardar cambios"}
            </button>

            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="aorra-profile-btn-secondary"
            >
              Regresar
            </button>
          </div>

          {/* Reset + Delete links */}
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', width: '100%', marginTop: 4 }}>
            <button
              type="button"
              onClick={() => setShowResetView(true)}
              disabled={Boolean(selectedBoard && selectedBoard.role !== 'owner')}
              title={selectedBoard && selectedBoard.role !== 'owner' ? 'Solo el propietario puede restablecer este tablero' : undefined}
              className="aorra-profile-reset-link"
              style={{ width: 'auto', fontSize: 12, color: '#FF8C00' }}
            >
              Restablecer tablero
            </button>
            <span style={{ color: '#ddd', fontSize: 12, display: 'flex', alignItems: 'center' }}>|</span>
            <button
              type="button"
              onClick={() => setShowDeleteView(true)}
              className="aorra-profile-reset-link"
              style={{ width: 'auto', fontSize: 12, color: '#B00000' }}
            >
              Eliminar cuenta
            </button>
          </div>
        </div>

        {/* CSS styles adjusted for vertical tightness to prevent screen overflow */}
        <style>{`
          .aorra-dialog-content.aorra-profile-modal-content {
            box-sizing: border-box;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            padding: 16px 24px !important;
            gap: 16px !important;
            width: min(440px, calc(100vw - 32px)) !important;
            max-width: calc(100vw - 32px) !important;
            max-height: calc(100vh - 32px) !important;
            max-height: calc(100dvh - 32px) !important;
            height: auto !important;
            background: #FFFFFF !important;
            box-shadow: -2px 0px 20px rgba(176, 179, 189, 0.25) !important;
            border-radius: 20px !important;
            border: none !important;
            outline: none !important;
            overflow: hidden !important;
          }

          .aorra-profile-modal-header {
            position: relative;
            display: flex !important;
            flex-direction: row !important;
            justify-content: center !important;
            align-items: center !important;
            padding: 0px !important;
            width: 100%;
            min-height: 33px;
            flex-shrink: 0;
          }

          .aorra-profile-modal-title {
            padding: 0 24px;
            font-family: 'Manrope', sans-serif !important;
            font-style: normal;
            font-weight: 700 !important;
            font-size: 20px !important;
            line-height: 28px !important;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center !important;
            color: #1B2559 !important;
          }

          .aorra-profile-modal-close {
            position: absolute;
            right: 0px;
            top: 6px;
            width: 20px;
            height: 20px;
            background: none;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .aorra-profile-modal-body {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 0px 4px 4px;
            gap: 14px;
            width: 100%;
            height: auto;
            min-height: 0;
            flex: 1 1 auto;
            overflow-y: auto;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
          }

          .aorra-profile-avatar-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 2px 0px;
            gap: 8px;
            width: 100%;
            flex-shrink: 0;
          }

          .aorra-profile-avatar-circle {
            width: 80px;
            height: 80px;
            background: #CDF7F7;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 8px rgba(0,0,0,0.05);
          }

          .aorra-profile-user-name {
            font-family: 'Manrope', sans-serif;
            font-style: normal;
            font-weight: 700;
            font-size: 20px;
            line-height: 26px;
            text-align: center;
            color: #396BE9;
          }

          .aorra-profile-form {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 0px;
            gap: 10px;
            width: 100%;
            flex-shrink: 0;
          }

          .aorra-profile-field {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            padding: 0px;
            gap: 4px;
            width: 100%;
            box-sizing: border-box;
          }

          .aorra-profile-field-label {
            font-family: 'Manrope', sans-serif !important;
            font-style: normal;
            font-weight: 800 !important;
            font-size: 13px !important;
            line-height: 18px !important;
            display: flex;
            align-items: center;
            color: #5C5C5C !important;
          }

          .aorra-profile-input-wrapper {
            box-sizing: border-box;
            display: flex;
            flex-direction: row;
            align-items: center;
            padding: 0px 12px;
            width: 100%;
            height: 44px;
            border: 1px solid #DDDDDD;
            border-radius: 14px;
            background: #FFFFFF;
            position: relative;
          }

          .aorra-profile-input {
            width: 100% !important;
            height: 100% !important;
            border: none !important;
            background: transparent !important;
            box-shadow: none !important;
            font-family: 'Manrope', sans-serif !important;
            font-weight: 400 !important;
            font-size: 16px !important;
            color: #5C5C5C !important;
            padding: 0px !important;
            outline: none !important;
          }

          .aorra-profile-select-trigger {
            width: 100% !important;
            height: 100% !important;
            border: none !important;
            background: transparent !important;
            box-shadow: none !important;
            font-family: 'Manrope', sans-serif !important;
            font-weight: 400 !important;
            font-size: 16px !important;
            color: #5C5C5C !important;
            padding: 0px !important;
            outline: none !important;
          }

          .aorra-country-trigger.aorra-profile-currency-trigger {
            box-sizing: border-box;
            min-height: 44px;
            height: 44px;
            padding: 0 12px;
            gap: 8px;
            border: 1px solid #DDDDDD;
            border-radius: 14px;
            background: #FFFFFF;
            color: #5C5C5C;
            font-size: 16px;
            font-weight: 400;
          }

          .aorra-profile-edit-icon {
            background: none;
            border: none;
            cursor: pointer;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-left: 8px;
          }

          .aorra-profile-buttons {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            padding: 0px;
            gap: 10px;
            width: 100%;
            margin-top: 6px;
          }

          .aorra-profile-btn-primary {
            width: 100%;
            height: 46px;
            background: #396BE9;
            border-radius: 14px;
            border: none;
            font-family: 'Manrope', sans-serif;
            font-weight: 700;
            font-size: 15px;
            color: #FFFFFF;
            cursor: pointer;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: opacity 0.15s ease;
          }

          .aorra-profile-btn-primary:hover {
            opacity: 0.95;
          }

          .aorra-profile-btn-secondary {
            width: 100%;
            height: 46px;
            background: #E1E9FC;
            border-radius: 14px;
            border: none;
            font-family: 'Manrope', sans-serif;
            font-weight: 700;
            font-size: 15px;
            color: #396BE9;
            cursor: pointer;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: opacity 0.15s ease;
          }

          .aorra-profile-btn-secondary:hover {
            opacity: 0.95;
          }

          .aorra-profile-reset-link {
            border: none;
            background: none;
            color: #FF3B30;
            font-family: 'Manrope', sans-serif;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            margin-top: 8px;
            width: 100%;
            transition: opacity 0.15s ease;
          }

          .aorra-profile-reset-link:hover {
            opacity: 0.8;
            text-decoration: underline;
          }
          /* Hide the default shadcn dialog close button */
          .aorra-profile-modal-content > button {
            display: none !important;
          }

          /* Radix Select Item Highlight override (orange to blue) */
          [data-radix-select-viewport] [role="option"][data-highlighted],
          [role="option"][data-highlighted] {
            background-color: #396BE9 !important;
            color: #FFFFFF !important;
            outline: none !important;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileSettings;
